import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { AppConfig } from '@luggagehero/shared/app-settings/data-access';
import { BankAccountCountryInfo, IPaymentRecord, IUser, PayoutProvider } from '@luggagehero/shared/interfaces';
import { SharedErrorService } from '@luggagehero/shared/services/error';
import { SharedNotificationService } from '@luggagehero/shared/services/notification';
import { SharedStorageService } from '@luggagehero/shared/services/storage';
import { SharedUserService } from '@luggagehero/shared/services/users';
import { SharedWindowService } from '@luggagehero/shared/services/window';
import { TranslateService } from '@ngx-translate/core';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { firstValueFrom, Observable } from 'rxjs';

import { BaseComponent } from '../../../core';
import { BusinessType, IPLookupService, PayoutService, StorageManagerService } from '../../../services/index';
import { PayoutVerificationStatus } from '../../../services/storage-manager/payout-verification-status';
import { BankAccountFormBaseComponent } from '../../ui/index';

const DEFAULT_PAYOUT_ACCOUNT_COUNTRY = 'US';

@Component({ template: '' })
export abstract class ManagePayoutMethodsBaseComponent extends BaseComponent implements OnInit {
  @Output() payoutMethodsChange = new EventEmitter<IPaymentRecord[]>();
  @Output() payoutVerificationStatusChange = new EventEmitter<PayoutVerificationStatus>();
  @Input() payoutCurrency: string;
  @ViewChild('newPayoutAccountModal') public newPayoutAccountModal: ModalDirective;
  @ViewChild('newPayoutAccountModalRef') public newPayoutAccountModalRef: ElementRef<HTMLElement>;
  @ViewChild('addBankAccountForm') public addBankAccountForm: BankAccountFormBaseComponent;
  @ViewChild('changeBankAccountForm') public changeBankAccountForm: BankAccountFormBaseComponent;
  @ViewChild('changeBankAccountModal') public changeBankAccountModal: ModalDirective;
  @ViewChild('confirmStripeConnectModal') public confirmStripeConnectModal: ModalDirective;
  public payoutMethods: IPaymentRecord[] = [];
  public isBankAccountFormVisible = false;
  public supportedCountries: BankAccountCountryInfo[];
  public payoutProviderAccountForm: FormGroup;
  public supportedPayoutProviders: PayoutProvider[];
  public availablePayoutProviders: PayoutProvider[];
  public selectedPayoutProvider: PayoutProvider;
  public payoutVerificationStatus: PayoutVerificationStatus;
  public isFailed = false;
  public errorMessage: string;
  public statusMessage: string;
  public businessType: BusinessType = BusinessType.company;
  public collectBusinessType = false;
  public businessLegalName = '';

  private isInitialized = false;
  private _isLoading = false;
  private _isPayoutAccountFormLoading = false;
  private _payoutProviderAccount: IPaymentRecord;
  private _selectedCountry: string;
  private _storageLocationId: string;

  constructor(
    private payoutService: PayoutService,
    private storageManagerService: StorageManagerService,
    private ipLookupService: IPLookupService,
    private userService: SharedUserService,
    private window: SharedWindowService,
    private notify: SharedNotificationService,
    private error: SharedErrorService,
    private translate: TranslateService,
    private cd: ChangeDetectorRef,
    private storageService: SharedStorageService,
  ) {
    super();
  }

  get canGoForward(): boolean {
    if (this.businessLegalName && this.businessLegalName.trim() !== '') {
      return true;
    }
    return false;
  }

  get canAddPayoutMethod(): boolean {
    if (this.payoutProviderAccount) {
      // For now only allow one payout method
      return false;
    }
    if (!this.selectedCountry) {
      return false;
    }
    return true;
  }

  get selectedCountry(): string {
    return this._selectedCountry;
  }
  @Input() set selectedCountry(value: string) {
    this._selectedCountry = value.toUpperCase();
  }

  get storageLocationId(): string {
    return this._storageLocationId;
  }
  @Input() set storageLocationId(value: string) {
    this._storageLocationId = value;
  }

  get selectedCountryName(): string {
    if (!this.selectedCountry || !this.supportedCountries) {
      return null;
    }

    const selectedCountry = this.supportedCountries.find((c) => c.countryCode === this.selectedCountry);
    if (!selectedCountry) {
      return null;
    }
    return selectedCountry.name;
  }

  get selectedCurrency(): string {
    // For now always use the first available payout method from the selected provider
    const payoutMethod = this.selectedPayoutProvider?.supportedPayoutMethods[0];

    // Get the country spec for the currently selected country
    const countrySpec = payoutMethod?.countrySpecs[this.selectedCountry];

    // Return the default currency for the country
    return countrySpec?.defaultCurrency || 'eur';
  }

  get user(): IUser {
    return this.userService.user;
  }

  get isStandardStripeAccountHolder(): boolean {
    if (!this.user) {
      return false;
    }
    return this.user.payoutProvider === 'stripe' && this.user.payoutProviderProtocol === 'standard';
  }

  get enableStandardStripeAccounts(): boolean {
    if (this.isStandardStripeAccountHolder) {
      return true;
    }
    return AppConfig.ENABLE_STRIPE_STANDARD_ACCOUNTS;
  }

  get payoutProviderAccount(): IPaymentRecord {
    return this._payoutProviderAccount;
  }
  set payoutProviderAccount(value: IPaymentRecord) {
    if (!this.isInitialized || this.isExistingPayoutAccount) {
      //
      // Don't allow bindings to change the account before initialization or if this
      // is an existing account
      //
      return;
    }
    this._payoutProviderAccount = value;
    this.cd.markForCheck();
  }

  get payoutProviderAccountName(): string {
    const provider = this.payoutProviderAccount ? this.payoutProviderAccount.provider : null;
    const providerData = this.payoutProviderAccount ? this.payoutProviderAccount.data : null;

    switch (provider) {
      case 'stripe':
        if (typeof providerData === 'object' && 'business_name' in providerData && providerData.business_name) {
          return (providerData as { business_name?: string })?.business_name;
        }
        if (
          typeof providerData === 'object' &&
          'company' in providerData &&
          providerData.company &&
          (providerData.company as { name?: string }).name
        ) {
          return (
            providerData as {
              company?: {
                name?: string;
              };
            }
          )?.company?.name;
        }
        if (
          typeof providerData === 'object' &&
          'business_profile' in providerData &&
          providerData.business_profile &&
          (
            providerData.business_profile as {
              name?: string;
            }
          ).name
        ) {
          return (
            providerData.business_profile as {
              name?: string;
            }
          ).name;
        }
        break;
    }

    return `<${this.translate.instant('BUSINESS_NOT_SPECIFIED')}>`;
  }

  get payoutProvider(): PayoutProvider {
    if (!this.payoutProviderAccount) {
      return null;
    }
    return this.supportedPayoutProviders?.find((p) => p.name === this.payoutProviderAccount.provider);
  }

  get isLoading(): boolean {
    return this._isLoading;
  }
  set isLoading(value: boolean) {
    if (!value) {
      this.statusMessage = '';
    }
    this._isLoading = value;
    this.cd.markForCheck();
  }

  get isPayoutAccountFormLoading(): boolean {
    return this._isPayoutAccountFormLoading;
  }
  set isPayoutAccountFormLoading(value: boolean) {
    this._isPayoutAccountFormLoading = value;
    this.cd.markForCheck();
  }
  get isStandardStripeAccountConnected(): boolean {
    return this.payoutProviderAccount && (this.payoutProviderAccount.data as { type?: string })?.type === 'standard';
  }

  get isCustomStripeAccountConnected(): boolean {
    return this.payoutProviderAccount && (this.payoutProviderAccount.data as { type?: string }).type === 'custom';
  }

  get isPayoutMethodsAvailable(): boolean {
    return this.payoutMethods && this.payoutMethods.length > 0;
  }

  get isChangeBankAccountAvailable(): boolean {
    if (this.payoutProviderAccount.provider !== 'stripe') {
      return false;
    }
    if ((this.payoutProviderAccount.data as { type?: string })?.type !== 'custom') {
      return false;
    }

    if (this.userService.isImpersonatedUser) {
      return false;
    }
    return true;
  }

  get stripeConnectAgreementUrl(): string {
    return this.payoutProviderAccount && (this.payoutProviderAccount.data as { country?: string })?.country
      ? `https://stripe.com/${(this.payoutProviderAccount.data as { country?: string }).country.toLowerCase()}/connect-account/legal`
      : `https://stripe.com/connect-account/legal`;
  }

  get isExistingPayoutAccount(): boolean {
    if (this.payoutProviderAccount && (this.payoutProviderAccount.data as { id?: unknown })?.id) {
      return true;
    }
    return false;
  }

  get existingPayoutAccountCountry(): string {
    switch (this.payoutProviderAccount?.provider) {
      case 'stripe':
        return (
          this.payoutProviderAccount.data as {
            country?: string;
          }
        )?.country;

      default:
        return null;
    }
  }

  get existingPayoutAccountDefaultCurrency(): string {
    switch (this.payoutProviderAccount?.provider) {
      case 'stripe':
        return (
          this.payoutProviderAccount.data as {
            default_currency?: string;
          }
        ).default_currency;

      default:
        return null;
    }
  }

  get isPayoutVerificationMissingInformation(): boolean {
    if (!this.payoutVerificationStatus) {
      return true;
    }
    return this.payoutVerificationStatus.code === 'MissingInformation';
  }

  get isPayoutVerificationSuccessful(): boolean {
    if (!this.payoutVerificationStatus) {
      return false;
    }
    return this.payoutVerificationStatus.code === 'Successful';
  }

  get isPayoutVerificationAwaitingManualReview(): boolean {
    if (!this.payoutVerificationStatus) {
      return false;
    }
    return this.payoutVerificationStatus.code === 'AwaitingManualReview';
  }

  get managePayoutAccountCta(): string {
    const provider = this.payoutProviderAccount ? this.payoutProviderAccount.provider : '';
    switch (provider) {
      case 'stripe':
        if (
          (
            this.payoutProviderAccount.data as {
              type?: string;
            }
          ).type === 'standard'
        ) {
          return 'MANAGE_YOUR_STRIPE_ACCOUNT';
        }
        break;
    }
    return 'UPDATE_YOUR_BUSINESS';
  }

  get verifyPayoutAccountCta(): string {
    const provider = this.payoutProviderAccount ? this.payoutProviderAccount.provider : '';
    switch (provider) {
      case 'stripe':
        if (
          (
            this.payoutProviderAccount.data as {
              type?: string;
            }
          ).type === 'standard'
        ) {
          return 'MANAGE_YOUR_STRIPE_ACCOUNT';
        }
        break;
    }
    return 'VERIFY_YOUR_BUSINESS';
  }

  async ngOnInit() {
    this.isLoading = true;

    let payoutVerificationStatusLoaded = false;
    const accountId = this.storageService.payoutProviderAccountId;
    if (accountId) {
      this.payoutVerificationStatus = await this.payoutService.getPayoutAccountVerificationStatus(accountId);
      payoutVerificationStatusLoaded = true;
      this.storageService.payoutProviderAccountId = null;
    }

    try {
      // TODO: Move this call to new service and have it return the payout verification status for each account
      const payoutProviderAccounts = await this.payoutService.getPayoutProviderAccounts();

      if (payoutProviderAccounts && payoutProviderAccounts.length > 0) {
        this._payoutProviderAccount = payoutProviderAccounts[0];
        this.payoutMethods = await this.payoutService.getPayoutMethods();

        if (!payoutVerificationStatusLoaded) {
          this.payoutVerificationStatus = await this.payoutService.getPayoutAccountVerificationStatus(
            this.payoutProviderAccount.id,
          );
        }
      } else {
        this.payoutMethods = [];
      }
      this.payoutVerificationStatusChange.emit(this.payoutVerificationStatus);
      this.payoutMethodsChange.emit([this.payoutProviderAccount]);
      this.isInitialized = true;
    } catch (err) {
      this.error.handleError(err, this.translate.instant('ERROR_LOADING_PAYOUT_METHODS') as string);
      this.errorMessage = this.translate.instant('ERROR_LOADING_PAYOUT_METHODS') as string;
      this.isFailed = true;
    }

    await this.loadSupportedCountries();

    this.isLoading = false;
  }

  async loadSupportedCountries() {
    try {
      const payoutProviders = await this.storageManagerService.getSupportedPayoutProviders();
      this.supportedPayoutProviders = payoutProviders.map((payoutProvider) => {
        // HACK: Add relative path to logo file name provided in config
        payoutProvider.logo = `../../../../assets/payout-providers/${payoutProvider.logo}`;

        return payoutProvider;
      });
    } catch {
      // Could not load payout providers
      this.errorMessage = this.translate.instant('ERROR_LOADING_PAYOUT_METHODS') as string;
      this.isFailed = true;
    }

    if (!this.supportedPayoutProviders?.length) {
      // No payout providers found
      return;
    }

    // Filter the list of supported countries based on payout provider support
    const countries = AppConfig.SUPPORTED_BANK_ACCOUNT_COUNTRIES.filter((c) => {
      for (const payoutProvider of this.supportedPayoutProviders) {
        // For now only consider one type of payout method per provider
        const payoutMethod = payoutProvider.supportedPayoutMethods[0];

        if (Object.keys(payoutMethod.countrySpecs).includes(c.countryCode)) {
          c.supportedCurrencies = payoutMethod.countrySpecs[c.countryCode].supportedCurrencies;
          c.defaultCurrency = payoutMethod.countrySpecs[c.countryCode].defaultCurrency;
          return true;
        }
      }
      return false;
    });

    if (countries.length > 0) {
      // Get translations for all country names and update each country in the list with its translated name
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      const countryNames = countries.map((c) => c.name);
      const translations = await firstValueFrom(this.translate.get(countryNames) as Observable<Record<string, string>>);
      countries.forEach((c) => (c.name = translations[c.name]));

      // Sort the list alphabetically by the translated names
      countries.sort((a, b) => a.name.localeCompare(b.name));
    }

    // Set the list of supported countries
    this.supportedCountries = countries;
    this.updateAvailablePayoutProviders();
  }

  updateAvailablePayoutProviders() {
    if (this.supportedPayoutProviders?.length > 0) {
      // Update the list of available providers based on the newly selected country
      this.availablePayoutProviders = this.supportedPayoutProviders.filter((payoutProvider) => {
        for (const payoutMethod of payoutProvider.supportedPayoutMethods) {
          if (Object.keys(payoutMethod.countrySpecs).includes(this._selectedCountry)) {
            return true;
          }
        }
        return false;
      });
    }

    if (this.availablePayoutProviders?.length > 0) {
      // Set the first available provider as selected by default
      this.selectedPayoutProvider = this.availablePayoutProviders[0];
    }

    this.cd.markForCheck();
  }

  getPayoutMethodType(payoutProviderAccount: IPaymentRecord): string {
    let type: string;

    switch (payoutProviderAccount.provider) {
      case 'stripe':
        if ((this.payoutProviderAccount.data as { type?: string })?.type === 'standard') {
          type = 'Stripe Account';
        } else if (
          this.payoutMethods[0]?.data &&
          typeof this.payoutMethods[0].data === 'object' &&
          'object' in this.payoutMethods[0].data &&
          typeof this.payoutMethods[0].data.object === 'string'
        ) {
          type = this.payoutMethods[0].data.object.toUpperCase();
        }
        break;
    }
    return type ? (this.translate.instant(type) as string) : '';
  }

  getPayoutMethodHeadline(payoutProviderAccount: IPaymentRecord): string {
    if (!this.isBankAccountSetupCompleted) {
      return `<${this.translate.instant('BANK_ACCOUNT_MISSING')}>`;
    }
    switch (payoutProviderAccount.provider) {
      case 'stripe':
        if ((this.payoutProviderAccount.data as { type?: string })?.type === 'standard') {
          return (this.payoutProviderAccount.data as { email?: string })?.email;
        }

        if (this.payoutMethods[0]?.data) {
          return (this.payoutMethods[0].data as { bank_name?: string }).bank_name;
        }

        if ((this.payoutProviderAccount.data as { type?: string })?.type === 'custom') {
          const externalAccounts = (
            this.payoutProviderAccount.data as { external_accounts?: { data?: { bank_name: string }[] } }
          ).external_accounts;
          if (externalAccounts?.data?.length > 0) {
            return `${externalAccounts.data[0].bank_name}`;
          }
        }
        break;
    }
    return this.translate.instant('PAYOUT_METHOD') as string;
  }

  get isBankAccountSetupCompleted(): boolean {
    const providerData = this.payoutProviderAccount ? this.payoutProviderAccount.data : null;
    let hasExternalAccount = false;

    if (providerData) {
      const externalAccounts = (providerData as { external_accounts?: { data: unknown[] } }).external_accounts;

      if (externalAccounts?.data?.length > 0) {
        hasExternalAccount = true;
      }
    }
    return this.payoutMethods[0]?.data || hasExternalAccount ? true : false;
  }

  getPayoutMethodDetails(payoutProviderAccount: IPaymentRecord): string {
    switch (payoutProviderAccount.provider) {
      case 'stripe':
        if ((this.payoutProviderAccount.data as { type?: string })?.type === 'standard') {
          return (this.payoutProviderAccount.data as { id?: string })?.id;
        }

        if (this.payoutMethods[0]?.data) {
          const accountEndingIn = this.translate.instant('ACCOUNT_NUMBER_ENDING_IN') as string;
          return `${accountEndingIn} ${(this.payoutMethods[0].data as { last4: string })?.last4}`;
        }

        if ((this.payoutProviderAccount.data as { type?: string })?.type === 'custom') {
          const accountEndingIn = this.translate.instant('ACCOUNT_NUMBER_ENDING_IN') as string;
          const externalAccounts = (
            this.payoutProviderAccount.data as { external_accounts?: { data: { last4: string }[] } }
          ).external_accounts;

          if (externalAccounts?.data?.length > 0 && externalAccounts.data[0].last4) {
            return `${accountEndingIn} ${externalAccounts.data[0].last4}`;
          }
        }
    }
    return '';
  }
  async createPayoutAccount() {
    this.isLoading = true;
    this.statusMessage = `${this.translate.instant('SETTING_UP_PAYOUT_METHOD')}`;
    this._payoutProviderAccount = await this.payoutService.addPayoutProviderAccount(
      this.user.email,
      this.selectedCountry,
      this.businessType,
      this.businessLegalName,
      this.storageLocationId,
      this.selectedPayoutProvider.name,
    );
    await this.managePayoutProviderAccount();
    return;
  }

  closeCreatePayoutProviderAccountModal() {
    this.newPayoutAccountModal.hide();
  }

  async addPayoutProviderAccount() {
    this.isLoading = true;

    this.updateAvailablePayoutProviders();

    if (!this.selectedCountry) {
      // Get the country where the users is currently located based on their IP address
      const ipLocation = await this.ipLookupService.locate();
      const userCountry = ipLocation.country_code.toUpperCase();

      // Use the user's country if it is supported, otherwise use the default country
      this.selectedCountry = this.supportedCountries.find((c) => c.countryCode === userCountry)
        ? userCountry
        : DEFAULT_PAYOUT_ACCOUNT_COUNTRY;
    }

    this.newPayoutAccountModal.show();

    this.isLoading = false;
  }

  async managePayoutProviderAccount() {
    if (!this.payoutProviderAccount) {
      return;
    }
    const provider = this.payoutProviderAccount
      ? this.supportedPayoutProviders.find((p) => p.name === this.payoutProviderAccount.provider)
      : this.selectedPayoutProvider;
    this.statusMessage = provider
      ? `${this.translate.instant('REDIRECTING_TO')} ${provider.displayName}`
      : `${this.translate.instant('REDIRECTING')}`;
    this.isLoading = true;

    const link = await this.payoutService.getManagePayoutAccountLink(this.payoutProviderAccount.id);

    this.storageService.payoutProviderAccountId = this.payoutProviderAccount.id;
    this.window.open(link.url, link.newTab ? '_blank' : '_self');

    if (link.newTab) {
      // The user is still here so reset the UI, but wait a bit to avoid flickering
      setTimeout(() => {
        this.isLoading = false;
      }, 1000);
    }
  }

  async delete(payoutMethod: IPaymentRecord<unknown>) {
    if (!confirm(this.translate.instant('CONFIRM_DELETE_PAYMENT_METHOD') as string)) {
      return;
    }
    this.isLoading = true;

    try {
      await this.payoutService.deletePayoutMethod(payoutMethod.id);

      const index = this.payoutMethods.indexOf(payoutMethod);
      if (index >= 0) {
        this.payoutMethods.splice(index, 1);
      }
    } catch (err) {
      this.error.handleError(err, this.translate.instant('ERROR_REMOVING_PAYOUT_METHOD') as string);
    }
    this.isLoading = false;
  }

  async connectWithStripe(confirmExistingAccount = true) {
    if (this.isStandardStripeAccountConnected && confirmExistingAccount) {
      this.confirmStripeConnectModal.show();
      return;
    }
    this.confirmStripeConnectModal.hide();

    this.isLoading = true;

    try {
      const code = await this.payoutService.getAuthorizationCode('stripe');

      const account = await this.payoutService.connectStripeAccount(code);
      if (account) {
        this.payoutProviderAccount = account;
        this.notify.success(this.translate.instant('STRIPE_ACCOUNT_CONNECTED') as string);
      }
    } catch (err) {
      this.error.handleError(err, this.translate.instant('ERROR_CONNECTING_STRIPE_ACCOUNT') as string);
    }
    this.isLoading = false;
  }
}
